import {
  createMultiStyleConfigHelpers,
  keyframes,
  MultiStyleType,
  StyleThemingProps,
  useMultiStyleConfig,
} from '@chakra-ui/react'
import { asChakraVariable } from '~/themes/colors/colors'
import { textStyleKeys } from '~/themes/textStyles'
import { getName } from '~/utils/objectUtils'

import { messagingColorKeys } from './_colors'

const { PendingMessageBubbleColors } = messagingColorKeys.Messaging

const pendingMessageBubbleParts = [
  'container',
  'bubble',
  'statusText',
  'retryText',
  'messageTextWrapper',
  'messageText',
  'messageImage',
] as const

export enum PendingMessageBubbleVariants {
  Sending = 'sending',
  SendingEnlarged = 'sendingEnlarged',
  Failed = 'failed',
  FailedEnlarged = 'failedEnlarged',
}

const helpers = createMultiStyleConfigHelpers(pendingMessageBubbleParts)

const GradientBackgroundKeyframes = keyframes`
0% {
  background-position: -1000px 0;
}
100% {
  background-position: 1000px 0;
}
/* Specify background size in keyframes animation */
0%, 100% {
  background-size: 1000px;
}
`

export const GradientBackgroundSendingAnimation = `${GradientBackgroundKeyframes} 3s linear infinite forwards`

export const PendingMessageBubbleStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      width: '100%',
    },
    bubble: {
      wordBreak: 'break-word',
      maxWidth: '55%',
      borderWidth: 0,
      padding: '8px',
      paddingLeft: '12px',
      paddingRight: '12px',
      borderTopRightRadius: '16px',
      borderBottomLeftRadius: '16px',
      borderTopLeftRadius: '16px',
      borderBottomRightRadius: '0px',
      boxSizing: 'border-box',
      backgroundColor:
        PendingMessageBubbleColors.PendingBubble.Sending.Background,
    },
    messageImage: {
      maxHeight: '150px',
      width: 'auto',
      maxWidth: '100%',
      borderRadius: '5px',
    },
    statusText: {
      color: PendingMessageBubbleColors.PendingBubble.Sending.StatusTextColor,
      textStyle: textStyleKeys.Body4,
      paddingRight: '8px',
    },
    retryText: {
      color: PendingMessageBubbleColors.PendingBubble.Failed.StatusTextColor,
      textStyle: textStyleKeys.Body4_Bold,
      cursor: 'pointer',
      display: 'inline',
      textDecoration: 'underline',
    },
    messageTextWrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '16px',
    },
    messageText: {
      color: PendingMessageBubbleColors.PendingBubble.Sending.TextColor,
      textStyle: textStyleKeys.Body3,
    },
  },
  variants: {
    [PendingMessageBubbleVariants.Sending]: {
      bubble: {
        backgroundSize: '1000px',
        background: `linear-gradient(to right, ${asChakraVariable(
          PendingMessageBubbleColors.PendingBubble.Sending
            .BackgroundGradientAlt,
        )} 0%, ${asChakraVariable(
          PendingMessageBubbleColors.PendingBubble.Sending.BackgroundGradient,
        )} 20%, ${asChakraVariable(
          PendingMessageBubbleColors.PendingBubble.Sending
            .BackgroundGradientAlt,
        )} 90%)`,
      },
    },
    [PendingMessageBubbleVariants.SendingEnlarged]: {
      bubble: {
        backgroundSize: '1000px',
        background: `linear-gradient(to right, ${asChakraVariable(
          PendingMessageBubbleColors.PendingBubble.Sending
            .BackgroundGradientAlt,
        )} 0%, ${asChakraVariable(
          PendingMessageBubbleColors.PendingBubble.Sending.BackgroundGradient,
        )} 20%, ${asChakraVariable(
          PendingMessageBubbleColors.PendingBubble.Sending
            .BackgroundGradientAlt,
        )} 90%)`,
        paddingLeft: '8px',
        paddingRight: '8px',
      },
      messageText: {
        display: 'block',
        lineHeight: '36px',
        fontSize: '42px',
      },
    },
    [PendingMessageBubbleVariants.Failed]: {
      bubble: {
        backgroundColor:
          PendingMessageBubbleColors.PendingBubble.Failed.Background,
      },
      statusText: {
        color: PendingMessageBubbleColors.PendingBubble.Failed.StatusTextColor,
        cursor: 'pointer',
      },
    },
    [PendingMessageBubbleVariants.FailedEnlarged]: {
      bubble: {
        backgroundColor:
          PendingMessageBubbleColors.PendingBubble.Failed.Background,
        paddingLeft: '8px',
        paddingRight: '8px',
      },
      statusText: {
        color: PendingMessageBubbleColors.PendingBubble.Failed.StatusTextColor,
      },
    },
  },
  defaultProps: {
    variant: PendingMessageBubbleVariants.Sending,
  },
})

const pendingMessageBubbleKeyName = getName({ PendingMessageBubbleStyle })

export const usePendingMessageBubbleStyles = (props?: StyleThemingProps) => {
  return useMultiStyleConfig(
    pendingMessageBubbleKeyName,
    props,
  ) as MultiStyleType<typeof pendingMessageBubbleParts>
}
